import { useEffect, useState } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

function Stat({ label, value }) {
  return (
    <div className="bg-ink-800 border border-white/5 rounded-2xl p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">{label}</div>
      <div className="mt-2 font-display text-3xl lg:text-4xl font-extrabold text-white tracking-tighter">{value}</div>
    </div>
  );
}

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/stats"), api.get("/history?limit=30")])
      .then(([s, h]) => { setStats(s.data); setHistory(h.data); })
      .catch(console.error);
  }, []);

  const recent = (stats?.recent_scores || []).map((r, i) => ({
    idx: i + 1, score: r.score_10,
    date: new Date(r.submitted_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }),
  }));
  const byCat = (stats?.by_category || []).map((c) => ({ name: c.category_name, nota: c.avg_score }));

  return (
    <div className="min-h-screen bg-ink relative">
      <SEO title="Mis estadísticas" path="/stats" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="relative">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono">Estadísticas</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[0.95]">Tu progreso</h1>

          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Tests" value={stats?.total_tests ?? 0} />
            <Stat label="Preguntas" value={stats?.total_questions ?? 0} />
            <Stat label="Nota media" value={stats?.avg_score ?? 0} />
            <Stat label="Precisión" value={`${stats?.accuracy ?? 0}%`} />
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-ink-800 border border-white/5 rounded-2xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-3 font-mono">Evolución de notas</div>
              <div className="h-64">
                {recent.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={recent}>
                      <CartesianGrid stroke="#1a2f24" strokeDasharray="3 3" />
                      <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                      <YAxis domain={[0, 10]} stroke="#475569" fontSize={11} />
                      <Tooltip contentStyle={{ backgroundColor: "#0b1611", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} labelStyle={{ color: "#f8fafc" }} />
                      <Line type="monotone" dataKey="score" stroke="#d4af37" strokeWidth={2} dot={{ fill: "#d4af37", r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-slate-500">Realiza tu primer test</div>
                )}
              </div>
            </div>

            <div className="bg-ink-800 border border-white/5 rounded-2xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-3 font-mono">Media por categoría</div>
              <div className="h-64">
                {byCat.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byCat}>
                      <CartesianGrid stroke="#1a2f24" strokeDasharray="3 3" />
                      <XAxis dataKey="name" stroke="#475569" fontSize={11} />
                      <YAxis domain={[0, 10]} stroke="#475569" fontSize={11} />
                      <Tooltip contentStyle={{ backgroundColor: "#0b1611", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} labelStyle={{ color: "#f8fafc" }} />
                      <Bar dataKey="nota" fill="#0a5c36" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-slate-500">Sin datos aún</div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 bg-ink-800 border border-white/5 rounded-2xl">
            <div className="px-5 py-4 border-b border-white/5">
              <h2 className="font-display text-base font-bold text-white">Historial</h2>
            </div>
            <div className="divide-y divide-white/5" data-testid="stats-history">
              {history.length === 0 && <div className="p-5 text-sm text-slate-500">Aún no hay historial.</div>}
              {history.map((h) => (
                <div key={h.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                  <div>
                    <div className="text-sm font-medium text-white">{h.category_name} · {h.mode}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      {new Date(h.submitted_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-extrabold text-gcgold tracking-tighter">{h.score_10}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{h.correct}/{h.total}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}
